/**
 * Cloudflare Turnstile — anonymous visitor verification
 *
 * ## Purpose
 * Reduce bot traffic by verifying anonymous visitors once per browser tab.
 * On success the edge sets an HttpOnly `ME_human` cookie so worker-proxy can
 * treat the request as human (`isHumanRequest()` checks `ME_jwt` OR `ME_human`).
 *
 * ## Entry point
 * `plugins/turnstile-human.client.js` calls `maybeRunTurnstile()` from
 * `window.onNuxtReady()` — once after SSR hydration on the first client visit.
 * Runs on all pages (no per-route middleware). Nuxt 2 does not re-run route
 * middleware on the client for the initial SSR landing page, so a client plugin
 * is the correct hook for first-load coverage.
 *
 * ## Flow
 * 1. `maybeRunTurnstile()` — skip if: feature flag off, WDIO test, known bot UA
 *    (`isbot`), logged in (`ME_jwt`), already verified this tab (`sessionStorage`),
 *    or already running.
 * 2. Load `challenges.cloudflare.com/turnstile/v0/api.js` (no async/defer;
 *    required when using `turnstile.ready()`).
 * 3. Render an invisible widget (`action: visit`, site key from env).
 * 4. On token → `POST /v2/challenges?type=turnstile` with header
 *    `cf-turnstile-invisible-token` (`packages/core/src/api/challenges.js`).
 * 5. worker-api-private validates the token with Cloudflare and responds with
 *    `Set-Cookie: ME_human`. Client sets `sessionStorage['me:turnstile:verified']`
 *    so we do not re-challenge in the same tab.
 *
 * ## Related (not in this file)
 * - Login Turnstile: `packages/core/src/components/mixins/turnstile-captcha.mixin.js`
 *   on the pages app (`action: login`), validated on `POST /account/login`.
 * - Constants: `packages/core/src/app.constants.js` (`TURNSTILE_ACTION_*`, cookie keys).
 * - Build env: `VUE_APP_TURNSTILE_INVISIBLE_SITE_KEY` in `channels2022/nuxt.config.js`.
 *
 * ## Debugging
 * - Staging keeps `console.log` (`drop_console` only in production builds).
 * - Network: Turnstile `api.js` + `POST .../v2/challenges?type=turnstile`.
 * - Application: `sessionStorage.getItem('me:turnstile:verified')`, `ME_human` cookie.
 */

import { isbot } from 'isbot';
import isWdioTesting from '@motionelements/core/src/helpers/wdio-testing.js';
import { isLoggedIn } from '@motionelements/core/src/services/account.service.js';
import { STORAGE_TURNSTILE_VERIFIED_KEY, TURNSTILE_ACTION_VISIT } from '@motionelements/core/src/app.constants.js';
import { createTurnstileChallenge } from '@motionelements/core/src/api/challenges.js';
import { isFeatureEnabled } from '@motionelements/core/src/components/mixins/feature-flag.mixin.js';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let widgetId = null;
let isRunning = false;
let widgetContainer = null;
let turnstileScriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const onError = () => {
      turnstileScriptPromise = null;
      reject(new Error('turnstile script load failed'));
    };

    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile/"]');

    if (existingScript) {
      if (window.turnstile) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => {
        existingScript.remove();
        onError();
      }, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    // Turnstile requires api.js without async/defer when using turnstile.ready()
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      onError();
    };
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

async function verifyTurnstileToken(token) {
  await createTurnstileChallenge(token);
  sessionStorage.setItem(STORAGE_TURNSTILE_VERIFIED_KEY, '1');
}

function getWidgetContainer() {
  if (!widgetContainer) {
    widgetContainer = document.createElement('div');
    widgetContainer.id = 'turnstile-human-widget';
    widgetContainer.setAttribute('aria-hidden', 'true');
    widgetContainer.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;clip:rect(0,0,0,0);';
    document.body.appendChild(widgetContainer);
  }

  return widgetContainer;
}

function removeWidget() {
  if (widgetId != null && window.turnstile) {
    window.turnstile.remove(widgetId);
    widgetId = null;
  }
}

function markTurnstileComplete() {
  isRunning = false;
  removeWidget();
}

function renderInvisibleTurnstile() {
  const start = Date.now() / 1000;

  removeWidget();

  widgetId = window.turnstile.render(getWidgetContainer(), {
    sitekey: process.env.VUE_APP_TURNSTILE_INVISIBLE_SITE_KEY,
    action: TURNSTILE_ACTION_VISIT,
    callback: async (token) => {
      console.log('======= turnstile DIFF ', Date.now() / 1000 - start);
      try {
        await verifyTurnstileToken(token);
      } catch (err) {
        console.log('turnstile invisible verify failed', err);
      } finally {
        markTurnstileComplete();
      }
    },
    'error-callback': (err) => {
      console.log('======== turnstile invisible error-callback =========', err);
      markTurnstileComplete();
    },
  });
}

async function runTurnstile() {
  if (!process.env.VUE_APP_TURNSTILE_INVISIBLE_SITE_KEY) {
    console.log('NO turnstile KEY');
    return;
  }

  isRunning = true;

  try {
    await loadTurnstileScript();
  } catch (err) {
    console.log('turnstile script load failed', err);
    isRunning = false;
    return;
  }

  if (!window.turnstile) {
    isRunning = false;
    return;
  }

  window.turnstile.ready(() => {
    console.log('TURNSTILE Ready');
    console.log('%c Cloudflare Turnstile human check (visit)', 'background: orange');
    renderInvisibleTurnstile();
  });
}

// eslint-disable-next-line import/prefer-default-export
export function maybeRunTurnstile() {
  if (!isFeatureEnabled('turnstile_captcha')) {
    return;
  }

  if (isWdioTesting()) {
    return;
  }

  if (isbot(navigator.userAgent)) {
    return;
  }

  if (isLoggedIn()) {
    return;
  }

  if (sessionStorage.getItem(STORAGE_TURNSTILE_VERIFIED_KEY)) {
    return;
  }

  if (isRunning) {
    return;
  }

  runTurnstile();
}
